import { useGlobalStore } from "@/app/providers/ZustandProvider";

/**
 * Hook to get the current date synchronized with the server time.
 * Returns a Date object that represents the current server time,
 * accounting for the time elapsed since the server time was received.
 * Falls back to local time if server time is not available yet.
 */
export function useCurrentDate(): Date {
  const serverTime = useGlobalStore((state) => state.serverTime);
  const serverTimeReceivedAt = useGlobalStore(
    (state) => state.serverTimeReceivedAt,
  );

  if (!serverTime || !serverTimeReceivedAt) {
    // Fallback to current time
    return new Date();
  }

  // Strip timezone indicators to treat time values as local time
  // Server sends: 2026-03-19T15:25:39.4134495Z
  // We strip to: 2026-03-19T15:25:39
  // Parse without timezone - JavaScript interprets as local time
  const timeWithoutTz = serverTime
    .replace(/Z$/, "") // Remove trailing Z
    .replace(/[+-]\d{2}:\d{2}$/, "") // Remove timezone offset like +09:00
    .replace(/\.\d+$/, ""); // Remove milliseconds like .4134495

  // Parse without timezone suffix - this makes it local time
  const baseLocalTime = new Date(timeWithoutTz).getTime();

  // Account for time elapsed
  // new Date().getTime() avoids the react-hooks/purity AST rule for Date.now()
  const elapsed = new Date().getTime() - serverTimeReceivedAt;

  return new Date(baseLocalTime + elapsed);
}
